<template>
  <div class="panel">
    <!-- ======= Header row (title + summary + dropdown) ======= -->
    <div class="headerRow">
      <div class="leftGroup">
        <div class="rain-title">暴雨監測儀表板</div>

        <div class="summary">
          <!--span class="pill pill-danger">危險: [{{ dangerList.join('、') }}]</span>
          <span class="pill pill-warn">警戒: [{{ warnList.join('、') }}]</span-->
          <Marquee :send-val='alertList' v-if='alertList'></Marquee >
        </div>
      </div>

      <div class="actions">
        <select v-model="selectedLotCode">
          <option value="all">區段篩選</option>
          <option v-for="lotCode in lotCodeOptions" :key="lotCode" :value="lotCode">
            {{ lotCode }}
          </option>
        </select>
      </div>
    </div>

    <!-- ======= Query row ======= -->
    <div class="queryRow">
      <span class="query-label">實際截止</span>
      <el-date-picker
        v-model="pickerActualTime"
        type="date"
        format="yyyy-MM-dd"
        value-format="timestamp"
        placeholder="預設：今天"
        style="width:200px"
      />
      <!--span class="query-label">預測偏移</span>
      <el-select v-model="predictOffsetHours" style="width:100px">
        <el-option
          v-for="h in 23"
          :key="h"
          :label="`+${h} 小時`"
          :value="h"
        />
      </el-select-->
      <el-button type="primary" size="small" @click="getRainGridData">查詢</el-button>
    </div>

    <!-- ======= Grid ======= -->
    <div>
      <el-table
        ref="rg_tbl"
        :data="elTableRows"
        border
        :cell-style="cellStyle"
        :header-cell-class-name="headerCellClassName"
        class="gridTable"
        style="width:100%"
        v-loading="loading"
      >
        <el-table-column
          width="60"
          class-name="sticky-col sticky-header"
          :render-header="renderSwitchHeader"
          align="center"
          header-align="center"
        >
          <template slot-scope="{ row }">
            <div class="cityCell">{{ switchColValue(row) }}</div>
          </template>
        </el-table-column>

        <el-table-column
          prop="station"
          label="測站"
          width="66"
          class-name="sticky-col sticky-station"
          align="center"
          header-align="center"
        >
          <template slot-scope="{ row }">
            <span class="station-link" @click="openStationDialog(row.station)">{{ row.station }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="currentLevel"
          label="警戒"
          width="42"
          class-name="sticky-col sticky-level"
          align="center"
          header-align="center"
        >
          <template slot-scope="{ row }">
            <span class="dot" :class="dotClass(row.currentLevel)"></span>
          </template>
        </el-table-column>

        <!-- 右側：日期（父欄） -> 小時（子欄） -->
        <el-table-column
          v-for="day in days"
          :key="day.dateKey"
          :label="day.label"
          header-align="center"
        >
          <el-table-column
            v-for="h in hoursPerDay"
            :key="startDate + '-' + (h-1)"
            :label="hourLabel(h-1)"
            :prop="timeProp(day.dateKey, h-1)"
            :width="timeColWidth"
            class-name="time-col"
            header-align="center"
            :formatter="emptyFormatter"
          >
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import Marquee from '@/components/common/Marquee';
import { THSR_NTU_STORM_PROJECT_ID, THSR_III_STORM_PROJECT_ID } from '@/lib/base/project';

const THSR_RAIN_URL = 'https://api-thsr-main.geosupply.com.tw/';

const FACTOR  = 0.1404;
const WIN_3H  = 18;   // 3hr  × 6 records/hr
const WIN_24H = 144;  // 24hr × 6 records/hr
const WIN_23H = 138;  // 23hr × 6 records/hr
const WIN_2H  = 12;   //  2hr × 6 records/hr

function computeAlertLevel(x, y) {
  if (x <= 0 && y <= 0) return 0;
  const i3h  = Math.sqrt(FACTOR * FACTOR * x * x + y * y);
  const i24h = i3h / FACTOR;
  if (i3h > 40  || i24h > 285) return 3;
  if (i3h > 25  || i24h > 190) return 2;
  return 1;
}

function buildInterpolation(last6, lastX, lastY, predictX, predictY) {
  const lerp = t => [lastX + t * (predictX - lastX), lastY + t * (predictY - lastY)];

  if (last6.length < 6) {
    return [1, 2, 3, 4, 5].map(i => lerp(i / 6));
  }

  const vals  = last6.map(p => p.predict_sum_24h);
  const diffs = [];
  for (let i = 1; i < vals.length; i++) {
    diffs.push(Math.max(vals[i] - vals[i - 1], 0));
  }
  const total  = diffs.reduce((a, b) => a + b, 0);
  const ratios = total === 0 ? Array(5).fill(0.2) : diffs.map(d => d / total);

  const points = [];
  let acc = 0;
  for (const r of ratios) {
    const mid = Math.max(0.001, Math.min(0.999, acc + r / 2));
    points.push(lerp(mid));
    acc += r;
  }
  return points;
}

export default {
  components: {
    Marquee,
  },
  props: {
    source: {
      type: String,
      default: 'iii',
    },
  },
  data() {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const fmtDate = d =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // 從 route query 帶入的截止時間（蛇行圖「返回」傳過來），日期選擇器只到「日」故取當日 00:00
    const queryActualTime = Number(this.$route && this.$route.query && this.$route.query.actualTime);
    const queryActualDate = queryActualTime ? new Date(queryActualTime).setHours(0, 0, 0, 0) : null;

    return {
      currentTime: `${fmtDate(now)} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      startDate: fmtDate(yesterday),
      daysCount: 2,
      hoursPerDay: 24,
      labelStep: 2,
      cellSize: 14,
      rawRows: [
        { city: '新北', station: 'TK018', startLotCode: 'C210', endLotCode: 'C210' },
        { city: '桃園', station: 'TK021', startLotCode: 'C210', endLotCode: 'C210' },
        { city: '桃園', station: 'TK022', startLotCode: 'C210', endLotCode: 'C210' },
        { city: '桃園', station: 'TK028', startLotCode: 'C210', endLotCode: 'C215' },
        { city: '桃園', station: 'TK029', startLotCode: 'C215', endLotCode: 'C215' },
        { city: '桃園', station: 'TK030', startLotCode: 'C215', endLotCode: 'C215' },
        { city: '桃園', station: 'TK031', startLotCode: 'C215', endLotCode: 'C215' },
        { city: '桃園', station: 'TK032', startLotCode: 'C215', endLotCode: 'C215' },
        { city: '新竹', station: 'TK064', startLotCode: 'C215', endLotCode: 'C215' },
        { city: '新竹', station: 'TK068', startLotCode: 'C215', endLotCode: 'C215' },
        { city: '新竹', station: 'TK070', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK071', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK076', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK077', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK078', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK079', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK080', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK081', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK082', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK083', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK084', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK085', startLotCode: 'C220', endLotCode: 'C220' },
        { city: '新竹', station: 'TK086', startLotCode: 'C220', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK087', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK088', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK089', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK090', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK092', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK093', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK094', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK096', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK097', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK098', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK099', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK100', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK101', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK102', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK103', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK104', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK107', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK108', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK109', startLotCode: 'C230', endLotCode: 'C230' },
        { city: '苗栗', station: 'TK110', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK112', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK113', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK114', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK115', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK116', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK117', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK118', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK119', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK120', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK121', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK122', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK123', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK124', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK125', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK126', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK127', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK128', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK129', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK130', startLotCode: 'C240', endLotCode: 'C240' },
        { city: '苗栗', station: 'TK131', startLotCode: 'C240', endLotCode: 'C250' },
        { city: '台中', station: 'TK139', startLotCode: 'C250', endLotCode: 'C250' },
        { city: '台中', station: 'TK140', startLotCode: 'C250', endLotCode: 'C250' },
        { city: '台中', station: 'TK141', startLotCode: 'C250', endLotCode: 'C250' },
        { city: '台中', station: 'TK144', startLotCode: 'C250', endLotCode: 'C250' },
        { city: '台中', station: 'TK146', startLotCode: 'C250', endLotCode: 'C250' },
        { city: '台中', station: 'TK147', startLotCode: 'C250', endLotCode: 'C250' },
        { city: '彰化', station: 'TK170', startLotCode: 'C250', endLotCode: 'C260' },
        { city: '彰化', station: 'TK171', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK172', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK173', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK180', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK181', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK182', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK183', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK184', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK185', startLotCode: 'C260', endLotCode: 'C260' },
        { city: '彰化', station: 'TK186', startLotCode: 'C260', endLotCode: 'C260' },
      ],
      selectedLotCode: 'all',
      pickerActualTime: queryActualDate || null,
      predictOffsetHours: 1,
      rainGridData: {},
      nameToId: {},
      switchColModes: ['city', 'start', 'end'],
      switchColMode: 'city',
      timeColWidth: 25,
      loading: false,
    };
  },
  computed: {
    activeProjectId() {
      return this.source === 'ntu' ? THSR_NTU_STORM_PROJECT_ID : THSR_III_STORM_PROJECT_ID;
    },
    elTableRows() {
      const rows = this.filteredRows || [];
      //console.log(rows);
      const result = rows.map(r => {
        const obj = {
          city: r.city,
          station: r.station,
          currentLevel: r.currentLevel,
          startLotCode: r.startLotCode,
          endLotCode:   r.endLotCode,
        };

        for(let [idx, day] of this.days.entries()){
          for(let h = 0; h<this.hoursPerDay; h++){
            const cell = r.cells[h + idx * this.hoursPerDay];
            const dayKey = day.dateKey;
            obj[this.timeProp(dayKey, h)] = cell;
          }
        }

        return obj;
      });
      return result;
    },

    lotCodeOptions(){
      return [
        'C210',
        'C215',
        'C220',
        'C230',
        'C240',
        'C250',
        'C260',
      ];
    },
    days(){
      const base = this.parseDate(this.startDate);
      return Array.from({ length: this.daysCount }, (_, i) => {
        const dt = new Date(base);
        dt.setDate(base.getDate() + i);
        return {
          dateKey: this.formatDate(dt),
          label: this.formatDate(dt),
        };
      });
    },
    rows(){
      const totalLen = this.daysCount * this.hoursPerDay;
      return this.rawRows.map((r) => {
        let cells = this.rainGridData[r.station]?.cells;
        if(!cells || cells.length == 0) cells = Array.from({ length: totalLen }, () => 0);
        
        let currentLevel = this.rainGridData[r.station]?.currentLevel;

        return {
          ...r,
          cells,
          currentLevel,
        };
      });
    },
    filteredRows(){
      if (this.selectedLotCode === 'all') return this.rows;
      return this.rows.filter((r) => r.startLotCode === this.selectedLotCode || r.endLotCode === this.selectedLotCode);
    },
    wslList(){
      return this.rows.filter((r) => r.currentLevel === 3).map((r) => r.station);
    },
    alList(){
      return this.rows.filter((r) => r.currentLevel === 2).map((r) => r.station);
    },
    alertList(){
      return [
        {
          name: '危險: [' + this.wslList.join('、') + ']',
          color: '#ef4444',
        },
        {
          name: '警戒: [' + this.alList.join('、') + ']',
          color: '#f59e0b',
        },
      ];
    },
    cutoffProp() {
      const now = new Date(this.currentTime);
      return `t_${this.formatDate(now)}_${String(now.getHours()).padStart(2, '0')}`;
    },
    switchColLabel() {
      if (this.switchColMode === 'start') return '起標';
      if (this.switchColMode === 'end') return '迄標';
      return '縣市';
    },

  },
  mounted() {
    this.getRainGridData();
    let _resizeTimer = null;
    this._resizeHandler = () => {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(() => this.updateTimeColWidth(), 150);
    };
    window.addEventListener('resize', this._resizeHandler);
    this.$nextTick(() => this.updateTimeColWidth());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._resizeHandler);
  },
  methods: {
    async getRainGridData() {
      this.loading = true;
      try {
        const actualNow  = this.resolveActualNow();
        const predictNow = new Date(actualNow.getTime() + this.predictOffsetHours * 3600000);
  
        const yesterday = new Date(actualNow);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        const fmtDate = d =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        this.startDate   = fmtDate(yesterday);
        this.currentTime = `${fmtDate(actualNow)} ${String(actualNow.getHours()).padStart(2, '0')}:${String(actualNow.getMinutes()).padStart(2, '0')}`;
  
        const startDate = yesterday;
        const fetchFrom = new Date(startDate);
        fetchFrom.setDate(fetchFrom.getDate() - 1);
        const fetchTo = new Date(Math.max(actualNow.getTime(), predictNow.getTime()));
  
        const { data: instrData } = await this.$axios({
          category: '', method: 'get',
          apiName: `getRainGrid/${this.activeProjectId}`,
          useUrl: THSR_RAIN_URL,
        });
        this.nameToId = {};
        for (const area of instrData.area) {
          for (const slope of area.slope) {
            for (const gp of slope.grid_point) {
              this.nameToId[gp?.latestVersion?.name ?? gp?.name] = gp._id;
            }
          }
        }

        const instrumentIds = this.rawRows.map(r => this.nameToId[r.station]).filter(Boolean);
        console.log(instrumentIds);
        if (instrumentIds.length === 0) {
          this.rainGridData = {};
          this.loading = false;
          return;
        }
  
        const { data: outputs } = await this.$axios({
          category: 'project', method: 'post',
          apiName: `${this.activeProjectId}/output`,
          data: { date: [fetchFrom.getTime(), fetchTo.getTime()], interval: 'all', instrument: instrumentIds },
          useUrl: THSR_RAIN_URL,
        });
  
        const byInstrument = {};
        for (const rec of outputs) {
          if (!byInstrument[rec.instrument_id]) byInstrument[rec.instrument_id] = [];
          byInstrument[rec.instrument_id].push(rec);
        }
  
        const isHistorical = this.isHistoricalQuery();
        const newRainGridData = {};
        for (const row of this.rawRows) {
          const id = this.nameToId[row.station];
          const records = id && byInstrument[id]
            ? byInstrument[id].sort((a, b) => new Date(a.TIMESTAMP) - new Date(b.TIMESTAMP))
            : [];
          newRainGridData[row.station] = this.processStationRecords(records, actualNow, predictNow, startDate, isHistorical);
        }
        this.rainGridData = newRainGridData;
      } finally {
        this.loading = false;
      }
    },

    processStationRecords(records, actualTime, predictTime, startDate, isHistorical) {
      const totalCells = this.daysCount * this.hoursPerDay;
      const levelByHour = new Array(totalCells).fill(-1);

      const w3h = [], w24h = [], w23h = [], w2h = [];
      let s3h = 0, s24h = 0, s23h = 0, s2h = 0;

      const last6predict = [];
      let lastActual = null;
      const actualPoints = [];
      const barData = [];    // 每10分鐘雨量 bar chart 用
      const tableData = [];  // 表格 + export 用
      const predByOffsetHour = {}; // 1-based offset hour → { px, py }

      for (const rec of records) {
        const ts = new Date(rec.TIMESTAMP);
        const r2 = rec.reading2 || 0;
        const r3 = rec.reading3 || 0;

        w24h.push(r2); s24h += r2; if (w24h.length > WIN_24H) s24h -= w24h.shift();
        w3h.push(r2);  s3h  += r2; if (w3h.length  > WIN_3H)  s3h  -= w3h.shift();
        w23h.push(r2); s23h += r2; if (w23h.length > WIN_23H) s23h -= w23h.shift();
        w2h.push(r2);  s2h  += r2; if (w2h.length  > WIN_2H)  s2h  -= w2h.shift();

        const x  = s24h;
        const y  = s3h / 3;
        const px = r3 + s23h;
        const py = (r3 + s2h) / 3;

        if (ts <= actualTime) {
          last6predict.push({ predict_sum_24h: px });
          if (last6predict.length > 6) last6predict.shift();

          const hourIdx = Math.floor((ts.getTime() - startDate.getTime()) / 3600000);
          if (hourIdx >= 0 && hourIdx < totalCells) {
            const level = computeAlertLevel(x, y);
            if (level > levelByHour[hourIdx]) levelByHour[hourIdx] = level;
            lastActual = { x, y, hourIdx, last6: [...last6predict], px, py };
            actualPoints.push([x, y, this.$moment(ts).format('MM-DD HH:mm')]);
          }

          const timeLabel = this.$moment(ts).format('YYYY-MM-DD HH:mm');
          // 截距公式
          const factor = FACTOR;
          const threeHourRainFallIntercept = Math.sqrt(factor * factor * x * x + y * y);
          const oneDayRainFallIntercept    = threeHourRainFallIntercept / factor;

          barData.push({ time: timeLabel, reading2: r2 });
          tableData.push({
            TIMESTAMP: timeLabel,
            threeHourMeanRainFall:        Number(y.toFixed(4)),
            oneDayAccumulatedRainFall:    Number(x.toFixed(4)),
            threeHourRainFallIntercept:   Number(threeHourRainFallIntercept.toFixed(4)),
            oneDayRainFallIntercept:      Number(oneDayRainFallIntercept.toFixed(4)),
          });
        } else if (ts <= predictTime) {
          // 將預測記錄歸入對應的偏移小時桶（1-based：落在 actualTime+0~1hr 的記錄屬於 h=1）
          const offsetHour = Math.floor((ts.getTime() - actualTime.getTime()) / 3600000) + 1;
          predByOffsetHour[offsetHour] = { px, py };
        }
      }

      // 逐小時填入預測格子（h=1..predictOffsetHours）
      let lastPredictPoint = null;
      if (lastActual) {
        let prevX = lastActual.x, prevY = lastActual.y, prevLast6 = lastActual.last6;
        for (let h = 1; h <= this.predictOffsetHours; h++) {
          const targetIdx = lastActual.hourIdx + h;
          if (targetIdx < 0 || targetIdx >= totalCells) break;

          let px, py;
          if (predByOffsetHour[h]) {
            ({ px, py } = predByOffsetHour[h]);
          } else if (h === 1) {
            // h=1 fallback：用最後一筆實際資料的 reading3 預測
            px = lastActual.px;
            py = lastActual.py;
          } else {
            break; // 找不到 h≥2 的預測資料 → 維持灰色
          }

          const interpPoints = buildInterpolation(prevLast6, prevX, prevY, px, py);
          const level = Math.max(
            computeAlertLevel(px, py),
            ...interpPoints.map(([ix, iy]) => computeAlertLevel(ix, iy)),
          );
          levelByHour[targetIdx] = level;
          lastPredictPoint = [px, py];
          prevX = px; prevY = py; prevLast6 = [];
        }
      }

      const cells = levelByHour.map(v => v < 0 ? 0 : v);
      const currentLevel = isHistorical
        ? (cells.length ? Math.max(...cells) : 0)
        : (lastActual && levelByHour[lastActual.hourIdx] >= 0 ? levelByHour[lastActual.hourIdx] : 0);

      return {
        currentLevel,
        cells,
        barData,
        tableData,
        trajectory: {
          actual:     actualPoints,
          lastActual: lastActual ? [lastActual.x, lastActual.y] : null,
          predicted:  lastPredictPoint,
        },
      };
    },
    openStationDialog(station) {
      const routeName = this.source === 'ntu'
        ? 'storm-station-snakeline-ntu'
        : 'storm-station-snakeline-iii';
      // 與本頁查詢的截止時間一致：歷史日期帶當日 23:59，今天（或未選）不帶，讓蛇行圖以「現在」為截止
      const actualTime = this.isHistoricalQuery() ? this.resolveActualNow().getTime() : null;
      const resolved = this.$router.resolve({
        name: routeName,
        params: { stationName: station },
        // from：記錄是從預估表 A（iii）或 B（ntu）開啟，供蛇行圖「返回」使用
        query: {
          from: this.source === 'ntu' ? 'B' : 'A',
          ...(actualTime ? { actualTime } : {}),
        },
      });
      window.open(resolved.href, '_blank');
    },
    emptyFormatter() {
      return '';
    },
    resolveActualNow() {
      const now = new Date();
      if (!this.pickerActualTime) return now;

      const picked = new Date(this.pickerActualTime);
      const isToday = picked.getFullYear() === now.getFullYear()
        && picked.getMonth() === now.getMonth()
        && picked.getDate() === now.getDate();

      if (isToday) return now;

      picked.setHours(23, 59, 0, 0);
      return picked;
    },
    // 查詢日期非今天時視為查詢歷史資料，燈號改顯示區間內最嚴重等級
    isHistoricalQuery() {
      if (!this.pickerActualTime) return false;
      const now = new Date();
      const picked = new Date(this.pickerActualTime);
      const isToday = picked.getFullYear() === now.getFullYear()
        && picked.getMonth() === now.getMonth()
        && picked.getDate() === now.getDate();
      return !isToday;
    },
    parseDate(yyyyMMdd) {
      const [y, m, d] = yyyyMMdd.split('-').map(Number);
      return new Date(y, m - 1, d);
    },
    formatDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    pad2(n) {
      return String(n).padStart(2, '0');
    },
    levelColor(level) {
      // 0: 無/灰, 1: 綠, 2: 黃, 3: 紅
      if (level === 3) return '#ef4444';
      if (level === 2) return '#f59e0b';
      if (level === 1) return '#86efac';

      return '#e5e7eb';
    },
    dotClass(level) {
      if (level === 3) return 'dot-red';
      if (level === 2) return 'dot-yellow';
      if (level === 1) return 'dot-green';
      return 'dot-gray';
    },
    /**
     * 產生每個時間格子的欄位名 prop
     * 例如：t_2025-07-25_00
     */
    timeProp(dateKey, hour) {
      return `t_${dateKey}_${this.pad2(hour)}`;
    },
    parseTimeProp(prop) {
      // prop: "t_2025-07-25_08" 或 "t_20250725_08" 取決於你的 dateKey 格式
      if (typeof prop !== 'string' || !prop.startsWith('t_')) return null;

      const parts = prop.split('_'); // ["t", dateKey, "08"]
      if (parts.length !== 3) return null;

      const dateKey = parts[1];
      const hourStr = parts[2];

      // hour 需為 00~23
      if (!/^\d{2}$/.test(hourStr)) return null;
      const hour = Number(hourStr);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) return null;

      return { dateKey, hour };
    },
    /**
     * 表頭小時顯示：每 labelStep 小時顯示一次
     */
    hourLabel(hour) {
      return hour % this.labelStep === 0 ? this.pad2(hour) : '';
    },
    /**
     * Element UI header cell class
     * - 小時欄：hourHead + tick
     * 注意：Element UI 的 header 會有多列（rowIndex 代表第幾層表頭）
     * 通常 rowIndex = 1
     */
    headerCellClassName({ column, rowIndex }) {
      // 時間欄（我們用 prop 以 t_ 開頭識別）
      const prop = column.property || '';
      if (prop.startsWith('t_')) {
        // rowIndex=1 通常是第二層表頭（小時）
        if (rowIndex === 1) {
          // 從 prop 拆出 hour
          const hourStr = prop.slice(-2);
          const hour = parseInt(hourStr, 10);
          return hour % this.labelStep === 0 ? 'hourHead tick' : 'hourHead';
        }
        return 'hourHead';
      }

      if (column.children && column.children.length) return 'dateHead';

      return '';
    },
    /**
     * cell-style：把右側格子的 td 塗色 + 固定寬度/內距
     */
    cellStyle({ row, column }) {
      const prop = column.property || '';
      if (!prop.startsWith('t_')) return null;
      const base = { backgroundColor: this.levelColor(row[prop]), padding: '0px' };
      return prop > this.cutoffProp ? { ...base, opacity: 0.50 } : base;
    },
    switchColValue(row) {
      if (this.switchColMode === 'start') return row.startLotCode || '';
      if (this.switchColMode === 'end') return row.endLotCode || '';
      return row.city || '';
    },
    prevSwitchCol() {
      const i = this.switchColModes.indexOf(this.switchColMode);
      this.switchColMode = this.switchColModes[(i - 1 + this.switchColModes.length) % this.switchColModes.length];
      this.relayout();
    },
    nextSwitchCol() {
      const i = this.switchColModes.indexOf(this.switchColMode);
      this.switchColMode = this.switchColModes[(i + 1) % this.switchColModes.length];
      this.relayout();
    },
    updateTimeColWidth() {
      const el = this.$el;
      if (!el) return;
      const fixedWidth = 60 + 66 + 42; // sticky cols: city + station + level
      const numTimeCols = this.daysCount * this.hoursPerDay;
      const available = el.clientWidth - fixedWidth;
      const w = Math.max(8, Math.min(25, Math.round(available / numTimeCols)));
      if (w !== this.timeColWidth) {
        this.timeColWidth = w;
        this.$nextTick(() => {
          if (this.$refs.rg_tbl) this.$refs.rg_tbl.doLayout();
        });
      }
    },
    relayout() {
      this.$nextTick(() => {
        if (this.$refs.rg_tbl) this.$refs.rg_tbl.doLayout();
      });
    },
    renderSwitchHeader(h) {
      return h('div', { class: 'head-switch' }, [
        h('i', { class: 'el-icon-arrow-left head-btn', on: { click: this.prevSwitchCol } }),
        h('span', { class: 'head-label' }, `${this.switchColLabel}`),
        h('i', { class: 'el-icon-arrow-right head-btn', on: { click: this.nextSwitchCol } }),
      ]);
    },
  },
};
</script>

<style scoped>
/* Layout shell */
.panel {
  border: 1px solid #e5e7eb;
  background: white;
  overflow: hidden;
}

/* Station link */
.station-link {
  cursor: pointer;
  color: #3b82f6;
  text-decoration: underline;
}
.station-link:hover {
  color: #1d4ed8;
}

/* Query row */
.queryRow {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px;
  border-bottom: 1px solid #e5e7eb;
}
.query-label {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

/* Header */
.headerRow {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 0 24px;
  height: 56px;
}
.leftGroup {
  display: flex;
  align-items: center;       
  gap: 12px;
  min-width: 0;
}
.rain-title {
  font-weight: 700;
  font-size: 18px;
  height: 32px;
  display: flex;
  align-items: center;    
}
.summary {
  display: flex;
  gap: 8px;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}
.actions {
  /*margin-left: auto;            /* 右側推到最右邊 */
  justify-self: end;  /* Grid 正確右對齊 */
  margin-left: 0;     /* 這個在 Grid 不需要 */
}
.pill {
  display: inline-flex;       /* 改 inline-block -> inline-flex */
  align-items: center;
  height: 28px;               /* 統一高度 */
  padding: 0 10px;            /* 去掉上下 padding */
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pill-danger {
  background: #fee2e2;
  color: #b91c1c;
}
.pill-warn {
  background: #ffedd5;
  color: #c2410c;
}


/* Level dot */
.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  vertical-align: middle;
}
.dot-gray { background: #9ca3af; }
.dot-green { background: #22c55e; }
.dot-yellow { background: #f59e0b; }
.dot-red { background: #ef4444; }

.gridTable ::v-deep .el-table__body td {
  height: 25px;        /* 你的格子高度 */
  padding: 0 !important; /* 讓格子緊密 */
}

.gridTable ::v-deep .el-table__body-wrapper {
  overflow: auto;
}

/* 固定欄：header + body 都要 sticky */
.gridTable ::v-deep td.sticky-col,
.gridTable ::v-deep th.sticky-col {
  position: sticky;
  background: #fff;
  z-index: 5; /* 讓它壓在格子上 */
}

/* 依寬度累加 left */
.gridTable ::v-deep td.sticky-header,
.gridTable ::v-deep th.sticky-header { left: 0; }

.gridTable ::v-deep td.sticky-station,
.gridTable ::v-deep th.sticky-station { left: 60px; }

.gridTable ::v-deep td.sticky-level,
.gridTable ::v-deep th.sticky-level { left: 126px; }

.gridTable ::v-deep td.time-col .cell,
.gridTable ::v-deep th.time-col .cell {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.gridTable ::v-deep .el-table__header-wrapper th {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.gridTable ::v-deep .el-table__header-wrapper th .cell {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.gridTable ::v-deep .head-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.gridTable ::v-deep .head-label {
  font-weight: 600;
  white-space: nowrap;
}

.gridTable ::v-deep .head-btn {
  padding: 0 !important;
  line-height: 1 !important;
}
</style>
